import { useEffect, useState } from "react";
import {
  Sparkles, Heart, Image as ImageIcon, Play, Bookmark, Star, Flame, Crown,
  Gem, Lock, Flower2, Camera, Music, MapPin, Coffee, Plane, Zap, Moon, Sun,
  Cloud, Gift, MessageCircle, Smile, X, Plus, Save, GripVertical, ArrowUp, ArrowDown,
  Trash2, RotateCcw, Edit3,
} from "lucide-react";
import { toast } from "sonner";

// Keep this in sync with JGFansPages.jsx HIGHLIGHT_ICONS
const ICONS = {
  sparkles: Sparkles, heart: Heart, image: ImageIcon, play: Play, bookmark: Bookmark,
  star: Star, fire: Flame, crown: Crown, diamond: Gem, lock: Lock, flower: Flower2,
  camera: Camera, music: Music, map: MapPin, coffee: Coffee, plane: Plane, zap: Zap,
  moon: Moon, sun: Sun, cloud: Cloud, gift: Gift, message: MessageCircle, smile: Smile,
};
const ICON_KEYS = Object.keys(ICONS);

const DEFAULTS = [
  { title: "VIP", icon: "sparkles" },
  { title: "Nuevo", icon: "heart" },
  { title: "Fotos", icon: "image" },
  { title: "Reels", icon: "play" },
  { title: "Favoritos", icon: "bookmark" },
];

const Icon = ({ name, className = "w-5 h-5" }) => {
  const Cmp = ICONS[name] || Sparkles;
  return <Cmp className={className} />;
};

/**
 * Highlights editor modal for a given creator.
 *
 * Props:
 *   - creator: { username, name }
 *   - onClose: () => void
 *   - onSaved: () => void (optional)
 *   - getAuthHeaders: () => object
 */
const AdminJGFansHighlightsModal = ({ creator, onClose, onSaved, getAuthHeaders }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `/api/admin/jgfans/creator/${creator.username}/highlights`,
        { headers: { ...getAuthHeaders() } }
      );
      const d = await r.json();
      setItems(d.highlights || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [creator.username]);

  const addItem = () => {
    if (items.length >= 12) {
      toast.error("Máximo 12 highlights");
      return;
    }
    setItems((p) => [...p, { id: `new-${Date.now()}`, title: "Nuevo", icon: "sparkles", coverUrl: "" }]);
  };

  const removeItem = (idx) => setItems((p) => p.filter((_, i) => i !== idx));

  const update = (idx, patch) => {
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
  };

  const resetDefaults = () => {
    if (!window.confirm("¿Restaurar los 5 highlights por defecto? Se perderán los actuales.")) return;
    setItems(DEFAULTS.map((d, i) => ({ id: `def-${i}`, ...d, coverUrl: "" })));
  };

  const save = async () => {
    // Validate
    for (const it of items) {
      if (!it.title?.trim()) {
        toast.error("Cada highlight necesita un título");
        return;
      }
    }
    setSaving(true);
    try {
      const r = await fetch(
        `/api/admin/jgfans/creator/${creator.username}/highlights`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({
            highlights: items.map((it) => ({
              id: it.id?.startsWith("new-") || it.id?.startsWith("def-") ? null : it.id,
              title: it.title.trim().slice(0, 24),
              icon: it.icon || "sparkles",
              coverUrl: it.coverUrl?.trim() || null,
            })),
          }),
        }
      );
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error al guardar");
        return;
      }
      toast.success("Highlights guardados");
      onSaved?.();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-6">
      <div className="bg-zinc-950 border border-white/10 rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10">
          <div>
            <h2 className="text-white font-bold text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-pink-400" />
              Highlights de @{creator.username}
            </h2>
            <p className="text-white/50 text-xs">
              Aparecen en el feed VIP de la modelo (máx 12). Arrastra el orden, cambia íconos y portadas.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white p-1.5 rounded-lg hover:bg-white/5"
            data-testid="hl-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Live preview */}
        <div className="px-5 py-4 border-b border-white/10 bg-black/40">
          <p className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Vista previa en feed</p>
          <div className="flex gap-4 overflow-x-auto no-scrollbar">
            {(items.length > 0 ? items : DEFAULTS).map((h, i) => (
              <div key={h.id || i} className="flex flex-col items-center gap-1.5 min-w-[64px] shrink-0">
                <div className="w-16 h-16 rounded-full p-[2px] bg-gradient-to-tr from-pink-500/70 via-purple-500/70 to-pink-500/70">
                  {h.coverUrl ? (
                    <div className="w-full h-full rounded-full bg-black p-[2px]">
                      <img src={h.coverUrl} alt="" className="w-full h-full rounded-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-full h-full rounded-full bg-black flex items-center justify-center text-pink-200 border border-white/10">
                      <Icon name={h.icon} />
                    </div>
                  )}
                </div>
                <span className="text-[11px] text-white/80 truncate max-w-[68px]">{h.title}</span>
              </div>
            ))}
            {items.length === 0 && (
              <p className="text-white/40 text-xs self-center">Sin highlights — se mostrarán los 5 por defecto.</p>
            )}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {loading ? (
            <p className="text-white/40 text-sm py-12 text-center">Cargando…</p>
          ) : items.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-white/50 text-sm mb-3">Esta modelo aún no tiene highlights personalizados.</p>
              <button
                onClick={resetDefaults}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white text-sm rounded-lg border border-white/10"
                data-testid="hl-load-defaults"
              >
                Cargar los 5 por defecto
              </button>
            </div>
          ) : (
            items.map((it, idx) => (
              <div
                key={it.id || idx}
                className="bg-white/5 border border-white/10 rounded-xl p-3"
                data-testid={`hl-row-${idx}`}
              >
                <div className="flex items-start gap-3">
                  {/* Reorder controls */}
                  <div className="flex flex-col gap-0.5 pt-1 text-white/40">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-0.5 hover:text-white disabled:opacity-30" title="Subir">
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <GripVertical className="w-4 h-4 text-white/20" />
                    <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="p-0.5 hover:text-white disabled:opacity-30" title="Bajar">
                      <ArrowDown className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Preview circle */}
                  <div className="shrink-0">
                    <div className="w-14 h-14 rounded-full p-[2px] bg-gradient-to-tr from-pink-500/70 via-purple-500/70 to-pink-500/70">
                      {it.coverUrl ? (
                        <div className="w-full h-full rounded-full bg-black p-[1.5px]">
                          <img src={it.coverUrl} alt="" className="w-full h-full rounded-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-full h-full rounded-full bg-black flex items-center justify-center text-pink-200 border border-white/10">
                          <Icon name={it.icon} className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Inputs */}
                  <div className="flex-1 min-w-0 space-y-2">
                    <input
                      value={it.title}
                      onChange={(e) => update(idx, { title: e.target.value })}
                      maxLength={24}
                      placeholder="Título (max 24)"
                      data-testid={`hl-title-${idx}`}
                      className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-pink-400/50"
                    />
                    <input
                      value={it.coverUrl || ""}
                      onChange={(e) => update(idx, { coverUrl: e.target.value })}
                      placeholder="URL de portada (opcional, ej: https://…)"
                      data-testid={`hl-cover-${idx}`}
                      className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-pink-400/50"
                    />
                    {/* Icon picker */}
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">Icono</p>
                      <div className="grid grid-cols-8 sm:grid-cols-12 gap-1.5">
                        {ICON_KEYS.map((k) => (
                          <button
                            key={k}
                            onClick={() => update(idx, { icon: k })}
                            data-testid={`hl-icon-${idx}-${k}`}
                            className={`aspect-square flex items-center justify-center rounded-lg border transition ${
                              it.icon === k
                                ? "bg-pink-500/20 border-pink-500/60 text-pink-200"
                                : "bg-black/30 border-white/10 text-white/60 hover:bg-white/5 hover:text-white"
                            }`}
                            title={k}
                          >
                            <Icon name={k} className="w-3.5 h-3.5" />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Delete */}
                  <button
                    onClick={() => removeItem(idx)}
                    data-testid={`hl-remove-${idx}`}
                    className="p-2 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg shrink-0"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/10 bg-black/40 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={addItem}
              disabled={items.length >= 12}
              data-testid="hl-add"
              className="px-3 py-1.5 bg-white/10 hover:bg-white/15 text-white text-sm rounded-lg border border-white/10 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" /> Añadir
            </button>
            <button
              onClick={resetDefaults}
              data-testid="hl-reset"
              className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 text-sm rounded-lg border border-white/10 inline-flex items-center gap-1.5"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Restaurar
            </button>
            <span className="text-[11px] text-white/40">{items.length} / 12</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-white/60 hover:text-white text-sm rounded-lg"
            >
              Cancelar
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="hl-save"
              className="px-5 py-1.5 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white text-sm font-bold rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Trigger button that opens the highlights editor for a given creator.
 */
export const HighlightsEditorButton = ({ creator, getAuthHeaders, onSaved }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={`hl-edit-${creator.username}`}
        className="text-xs text-white/50 hover:text-pink-300 inline-flex items-center gap-1"
      >
        <Edit3 className="w-3 h-3" /> Highlights
      </button>
      {open && (
        <AdminJGFansHighlightsModal
          creator={creator}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setOpen(false)}
          onSaved={onSaved}
        />
      )}
    </>
  );
};

export default AdminJGFansHighlightsModal;
